import { createSlice } from "@reduxjs/toolkit";

export const audioCallSlice = createSlice({
  name: "audioCall",
  initialState: {
    callStatus: "idle", // idle | ringing_customer | connected | feedback
    phoneNumber: "",
    customerName: "",
    purchaseCode: "",
    callDuration: 0,
    agentDisplayName: "",
    userName: "",
    isLoggedIn: false,
    popoverOpen: false,
    feedbackData: {
      disposition_1_id: null,
      disposition_2_id: null,
      disposition3: "",
      callback_date: "",
      callnotes: "",
    },
    feedbackLoading: false,
    loading: false,
    error: null,
    isConfigured: false,
    disposition1Options: [],
    disposition2Options: [],
    dispositionsLoading: false,
    agentBusyOnAnotherDevice: false,
    busyWithUser: null,
    remoteCallStatus: null, // Stores the actual call_status from the API (idle, on_call, disposition_pending)
  },
  reducers: {
    // Initiate call
    initiateCall: (state, action) => {
      state.phoneNumber = action.payload.phoneNumber;
      state.customerName = action.payload.customerName || "";
      state.callStatus = "ringing_customer";
      state.popoverOpen = true;
      state.loading = true;
    },
    callConnected: (state) => {
      state.callStatus = "connected";
      state.callDuration = 0;
      state.loading = false;
    },
    callFailed: (state, action) => {
      state.callStatus = "idle";
      state.phoneNumber = "";
      state.customerName = "";
      state.popoverOpen = false;
      state.loading = false;
      state.error = action.payload;
    },

    // Hangup
    hangupCall: (state) => {
      state.loading = true;
    },
    hangupSuccess: (state) => {
      state.callStatus = "feedback";
      state.callDuration = 0;
      state.loading = false;
    },

    // Feedback
    submitCallFeedback: (state) => {
      state.feedbackLoading = true;
    },
    feedbackSuccess: (state) => {
      state.callStatus = "idle";
      state.phoneNumber = "";
      state.customerName = "";
      state.purchaseCode = "";
      state.callDuration = 0;
      state.popoverOpen = false;
      state.feedbackLoading = false;
      state.feedbackData = {
        disposition_1_id: null,
        disposition_2_id: null,
        disposition3: "",
        callback_date: "",
        callnotes: "",
      };
    },
    feedbackFailed: (state) => {
      state.feedbackLoading = false;
    },

    // Fetch dispositions
    fetchDispositions: (state) => {
      state.dispositionsLoading = true;
    },
    fetchDispositionsSuccess: (state, action) => {
      state.disposition1Options = action.payload.disposition1;
      state.disposition2Options = action.payload.disposition2;
      state.dispositionsLoading = false;
    },
    fetchDispositionsFailed: (state) => {
      state.dispositionsLoading = false;
    },

    // Status check (on page load)
    checkAgentStatus: () => {},
    statusCheckSuccess: (state, action) => {
      const { call_status, current_call, agent_name, user_name, is_configured, device_id, is_logged_in } = action.payload;
      
      // Store the remote call status
      state.remoteCallStatus = call_status || null;
      
      // Update login status
      state.isLoggedIn = is_logged_in || false;
      
      // Update isConfigured flag
      if (typeof is_configured === "boolean") {
        state.isConfigured = is_configured;
      }
      
      // Get current device_id from localStorage
      const currentDeviceId = localStorage.getItem("device_uuid");
      
      // Check if agent is logged in on another device
      if (is_logged_in && device_id && currentDeviceId && device_id !== currentDeviceId) {
        state.agentBusyOnAnotherDevice = true;
        // If on call, store the customer name
        if (call_status === "on_call" && current_call) {
          state.busyWithUser = current_call.customer_name || null;
        } else {
          state.busyWithUser = null;
        }
      } else {
        state.agentBusyOnAnotherDevice = false;
        state.busyWithUser = null;
      }
      
      if (agent_name) {
        state.agentDisplayName = agent_name;
      }
      
      if (user_name) {
        state.userName = user_name;
      }
      
      // Only show call status if it's the same device
      if (device_id === currentDeviceId) {
        if (call_status === "on_call" && current_call) {
          // Only reset duration if transitioning from a different state (new call)
          // Don't reset if already connected (to preserve timer during polling)
          const isNewCall = state.callStatus !== "connected";
          
          state.callStatus = "connected";
          state.customerName = current_call.customer_name || "";
          state.phoneNumber = current_call.customer_mobile_last4
            ? `****${current_call.customer_mobile_last4}`
            : "";
          state.purchaseCode = current_call.purchase_code || "";
          
          // Only reset duration for new calls, not during status polling
          if (isNewCall) {
            state.callDuration = 0;
            // Only open popover for new calls, not during polling
            state.popoverOpen = true;
          }
        } else if (call_status === "disposition_pending" && current_call) {
          // Only open feedback popover if transitioning from connected state
          const isNewFeedback = state.callStatus !== "feedback";
          
          state.callStatus = "feedback";
          state.customerName = current_call.customer_name || "";
          state.phoneNumber = current_call.customer_mobile_last4
            ? `****${current_call.customer_mobile_last4}`
            : "";
          state.purchaseCode = current_call.purchase_code || "";
          
          // Only open popover for new feedback requests
          if (isNewFeedback) {
            state.popoverOpen = true;
          }
        }
      }
    },

    // UI state
    setPopoverOpen: (state, action) => {
      state.popoverOpen = action.payload;
    },
    setFeedbackData: (state, action) => {
      state.feedbackData = action.payload;
    },
    setCallDuration: (state, action) => {
      state.callDuration = action.payload;
    },
    setAgentDisplayName: (state, action) => {
      state.agentDisplayName = action.payload;
    },
    setUserName: (state, action) => {
      state.userName = action.payload;
    },
    resetCallState: (state) => {
      state.callStatus = "idle";
      state.phoneNumber = "";
      state.customerName = "";
      state.purchaseCode = "";
      state.callDuration = 0;
      state.popoverOpen = false;
      state.feedbackData = {
        disposition_1_id: null,
        disposition_2_id: null,
        disposition3: "",
        callback_date: "",
        callnotes: "",
      };
      state.feedbackLoading = false;
      state.loading = false;
    },
    clearBusyStatus: (state) => {
      state.agentBusyOnAnotherDevice = false;
      state.busyWithUser = null;
    },
  },
});

export const {
  initiateCall,
  callConnected,
  callFailed,
  hangupCall,
  hangupSuccess,
  submitCallFeedback,
  feedbackSuccess,
  feedbackFailed,
  checkAgentStatus,
  statusCheckSuccess,
  fetchDispositions,
  fetchDispositionsSuccess,
  fetchDispositionsFailed,
  setPopoverOpen,
  setFeedbackData,
  setCallDuration,
  setAgentDisplayName,
  setUserName,
  resetCallState,
  clearBusyStatus,
} = audioCallSlice.actions;

export default audioCallSlice.reducer;
